import React, { useState, useMemo } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    useToast,
    Text,
    InputGroup,
    InputRightElement,
    IconButton,
    Box,
    Progress,
    List,
    ListItem,
    ListIcon,
    HStack,
    Divider
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { fetchWithAuth } from '../utils/fetchWithAuth';

// Password validation rules
const validatePasswordRequirements = (password) => {
    return {
        length: password.length >= 12,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /\d/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        notCommon: !['password', '12345678', 'qwerty'].some(common => 
            password.toLowerCase().includes(common)
        )
    };
};

// Calculate password strength (0-100)
const calculatePasswordStrength = (password, requirements) => {
    if (!password) return 0;
    
    const weights = {
        length: 20,
        uppercase: 15,
        lowercase: 15,
        number: 15,
        special: 20,
        notCommon: 15
    };
    
    let strength = 0;
    Object.keys(requirements).forEach(key => {
        if (requirements[key]) strength += weights[key];
    });
    
    return strength;
};

// Get strength color and label
const getStrengthInfo = (strength) => {
    if (strength === 0) return { color: 'gray.300', label: '', textColor: 'gray.500' };
    if (strength < 40) return { color: 'red.500', label: 'Weak', textColor: 'red.600' };
    if (strength < 70) return { color: 'orange.400', label: 'Fair', textColor: 'orange.600' };
    if (strength < 100) return { color: 'yellow.400', label: 'Good', textColor: 'yellow.700' };
    return { color: 'green.500', label: 'Strong', textColor: 'green.600' };
};

function PasswordRequirementItem({ met, children }) {
    return (
        <ListItem fontSize="sm" color={met ? 'green.600' : 'gray.500'}>
            <HStack spacing={2} align="start">
                <ListIcon
                    as={met ? CheckCircleIcon : WarningIcon}
                    color={met ? 'green.500' : 'gray.400'}
                    mt="2px"
                />
                <Text>{children}</Text>
            </HStack>
        </ListItem>
    );
}

function ChangePasswordModal({ isOpen, onClose }) {
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [touched, setTouched] = useState({
        new_password: false,
        confirm_password: false
    });
    const toast = useToast();

    // Calculate password requirements and strength
    const requirements = useMemo(() => 
        validatePasswordRequirements(formData.new_password),
        [formData.new_password]
    );
    
    const strength = useMemo(() => 
        calculatePasswordStrength(formData.new_password, requirements),
        [formData.new_password, requirements]
    );
    
    const strengthInfo = useMemo(() => 
        getStrengthInfo(strength),
        [strength]
    );

    const allRequirementsMet = useMemo(() => 
        Object.values(requirements).every(Boolean),
        [requirements]
    );

    const passwordsMatch = formData.new_password === formData.confirm_password;
    const canSubmit = allRequirementsMet && 
                      passwordsMatch && 
                      formData.current_password.length > 0 &&
                      formData.new_password !== formData.current_password;

    const handleReset = () => {
        setFormData({
            current_password: '',
            new_password: '',
            confirm_password: ''
        });
        setTouched({
            new_password: false,
            confirm_password: false
        });
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!canSubmit) return;

        setIsLoading(true);
        try {
            const response = await fetchWithAuth('/api/auth/change-password', {
                method: 'POST',
                body: {  // ← Pass as object, fetchWithAuth will stringify
                    current_password: formData.current_password,
                    new_password: formData.new_password
                }
            });

            if (response.ok) {
                toast({
                    title: 'Password changed successfully',
                    description: 'Your password has been updated',
                    status: 'success',
                    duration: 3000,
                    isClosable: true
                });
                handleClose();
            } else {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to change password');
            }
        } catch (error) {
            toast({
                title: 'Error changing password',
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg" closeOnOverlayClick={!isLoading}>
            <ModalOverlay />
            <ModalContent>
                <form onSubmit={handleSubmit}>
                    <ModalHeader>Change Password</ModalHeader>
                    <ModalCloseButton isDisabled={isLoading} />
                    <ModalBody>
                        <VStack spacing={5} align="stretch">
                            {/* Current Password */}
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="semibold">
                                    Current Password
                                </FormLabel>
                                <InputGroup>
                                    <Input
                                        type={showPasswords.current ? 'text' : 'password'}
                                        value={formData.current_password}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            current_password: e.target.value
                                        })}
                                        placeholder="Enter your current password"
                                    />
                                    <InputRightElement>
                                        <IconButton
                                            size="sm"
                                            variant="ghost"
                                            icon={showPasswords.current ? <ViewOffIcon /> : <ViewIcon />}
                                            onClick={() => setShowPasswords({
                                                ...showPasswords,
                                                current: !showPasswords.current
                                            })}
                                            tabIndex={-1}
                                        />
                                    </InputRightElement>
                                </InputGroup>
                            </FormControl>

                            <Divider />

                            {/* New Password */}
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="semibold">
                                    New Password
                                </FormLabel>
                                <InputGroup>
                                    <Input
                                        type={showPasswords.new ? 'text' : 'password'}
                                        value={formData.new_password}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            new_password: e.target.value
                                        })}
                                        onBlur={() => setTouched({ ...touched, new_password: true })}
                                        placeholder="Enter new password"
                                        borderColor={
                                            touched.new_password 
                                                ? (allRequirementsMet ? 'green.300' : 'red.300')
                                                : 'gray.200'
                                        }
                                    />
                                    <InputRightElement>
                                        <IconButton
                                            size="sm"
                                            variant="ghost"
                                            icon={showPasswords.new ? <ViewOffIcon /> : <ViewIcon />}
                                            onClick={() => setShowPasswords({
                                                ...showPasswords,
                                                new: !showPasswords.new
                                            })}
                                            tabIndex={-1}
                                        />
                                    </InputRightElement>
                                </InputGroup>

                                {/* Password Strength Indicator */}
                                {formData.new_password && (
                                    <Box mt={2}>
                                        <HStack justify="space-between" mb={1}>
                                            <Text fontSize="xs" fontWeight="medium" color="gray.600">
                                                Password Strength
                                            </Text>
                                            <Text 
                                                fontSize="xs" 
                                                fontWeight="bold" 
                                                color={strengthInfo.textColor}
                                            >
                                                {strengthInfo.label}
                                            </Text>
                                        </HStack>
                                        <Progress 
                                            value={strength} 
                                            size="sm" 
                                            colorScheme={
                                                strength < 40 ? 'red' : 
                                                strength < 70 ? 'orange' : 
                                                strength < 100 ? 'yellow' : 'green'
                                            }
                                            borderRadius="full"
                                        />
                                    </Box>
                                )}
                            </FormControl>

                            {/* Password Requirements */}
                            <Box 
                                bg="gray.50" 
                                p={4} 
                                borderRadius="md" 
                                border="1px" 
                                borderColor="gray.200"
                            >
                                <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={2}>
                                    PASSWORD REQUIREMENTS:
                                </Text>
                                <List spacing={1.5}>
                                    <PasswordRequirementItem met={requirements.length}>
                                        At least 12 characters
                                    </PasswordRequirementItem>
                                    <PasswordRequirementItem met={requirements.uppercase}>
                                        One uppercase letter (A-Z)
                                    </PasswordRequirementItem>
                                    <PasswordRequirementItem met={requirements.lowercase}>
                                        One lowercase letter (a-z)
                                    </PasswordRequirementItem>
                                    <PasswordRequirementItem met={requirements.number}>
                                        One number (0-9)
                                    </PasswordRequirementItem>
                                    <PasswordRequirementItem met={requirements.special}>
                                        One special character (!@#$%^&*)
                                    </PasswordRequirementItem>
                                    <PasswordRequirementItem met={requirements.notCommon}>
                                        Not a common password
                                    </PasswordRequirementItem>
                                </List>
                            </Box>

                            {/* Confirm Password */}
                            <FormControl 
                                isRequired
                                isInvalid={touched.confirm_password && formData.confirm_password && !passwordsMatch}
                            >
                                <FormLabel fontSize="sm" fontWeight="semibold">
                                    Confirm New Password
                                </FormLabel>
                                <InputGroup>
                                    <Input
                                        type={showPasswords.confirm ? 'text' : 'password'}
                                        value={formData.confirm_password}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            confirm_password: e.target.value
                                        })}
                                        onBlur={() => setTouched({ ...touched, confirm_password: true })}
                                        placeholder="Re-enter new password"
                                        borderColor={
                                            touched.confirm_password && formData.confirm_password
                                                ? (passwordsMatch ? 'green.300' : 'red.300')
                                                : 'gray.200'
                                        }
                                    />
                                    <InputRightElement>
                                        <IconButton
                                            size="sm"
                                            variant="ghost"
                                            icon={showPasswords.confirm ? <ViewOffIcon /> : <ViewIcon />}
                                            onClick={() => setShowPasswords({
                                                ...showPasswords,
                                                confirm: !showPasswords.confirm
                                            })}
                                            tabIndex={-1}
                                        />
                                    </InputRightElement>
                                </InputGroup>
                                {touched.confirm_password && formData.confirm_password && !passwordsMatch && (
                                    <Text fontSize="xs" color="red.500" mt={1}>
                                        Passwords do not match
                                    </Text>
                                )}
                                {touched.confirm_password && passwordsMatch && formData.confirm_password && (
                                    <HStack mt={1} spacing={1}>
                                        <CheckCircleIcon color="green.500" boxSize="12px" />
                                        <Text fontSize="xs" color="green.600">
                                            Passwords match
                                        </Text>
                                    </HStack>
                                )}
                            </FormControl>

                            {/* Warning if new password same as current */}
                            {formData.new_password && 
                             formData.new_password === formData.current_password && (
                                <Box 
                                    bg="orange.50" 
                                    border="1px" 
                                    borderColor="orange.200" 
                                    p={3} 
                                    borderRadius="md"
                                >
                                    <HStack spacing={2}>
                                        <WarningIcon color="orange.500" />
                                        <Text fontSize="sm" color="orange.700">
                                            New password must be different from current password
                                        </Text>
                                    </HStack>
                                </Box>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button 
                            variant="ghost" 
                            mr={3} 
                            onClick={handleClose}
                            isDisabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            type="submit"
                            isLoading={isLoading}
                            isDisabled={!canSubmit}
                        >
                            Change Password
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
}

export default ChangePasswordModal;